import React, { useState, useEffect, useContext } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../Context/AppContextProvide';
import { setUser as persistUser } from '../../../helpers';
import { getVersion, updateSeenVersion } from '../../../Actions';
import { CHANGELOG } from './changelog';
import {
  startTour,
  startNavTour,
  startHeaderSearchTour,
  startQrTour,
  resumePrintedIndicatorTour,
  startDuplicatesTour,
  startFeatureRequestsTour,
  startHelpCatalogTour,
  startTimezoneTour,
} from './tours';

export default function WhatsNewModal() {
  const { isLoggedOut, user, setUser, whatsNewOpen, setWhatsNewOpen, setTourMenuOpen } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [release, setRelease] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const history = useHistory();

  useEffect(() => {
    if (isLoggedOut) return;

    getVersion()
      .then((result) => {
        const appVersion = result?.data?.app_version;
        if (!appVersion) return;
        if (!CHANGELOG[appVersion]) return;

        setCurrentVersion(appVersion);
        setRelease(CHANGELOG[appVersion]);

        // Don't auto-prompt hidden/internal users with the "What's Changed" modal (they can still
        // open it manually from the menu).
        if (user?.last_seen_version !== appVersion && user?.onboarding_completed && !user?.is_hidden) {
          setOpen(true);
        }
      })
      .catch(() => {});
  }, [isLoggedOut]);

  useEffect(() => {
    if (whatsNewOpen) {
      setOpen(true);
      setWhatsNewOpen(false);
    }
  }, [whatsNewOpen]);

  function handleClose() {
    if (currentVersion) {
      updateSeenVersion(currentVersion).catch(() => {});
      const updatedUser = { ...user, last_seen_version: currentVersion };
      setUser(updatedUser);
      persistUser(updatedUser);
    }
    setOpen(false);
  }

  function handleTour() {
    handleClose();
    startTour(history, setTourMenuOpen);
  }

  function handleFeatureTour(tourKey) {
    handleClose();
    switch (tourKey) {
      case 'nav':              startNavTour(history, setTourMenuOpen); break;
      case 'headerSearch':     startHeaderSearchTour(history, setTourMenuOpen); break;
      case 'qr':               startQrTour(history); break;
      case 'printedIndicator': resumePrintedIndicatorTour(history); break;
      case 'duplicates':       startDuplicatesTour(history); break;
      case 'featureRequests':  startFeatureRequestsTour(history, setTourMenuOpen); break;
      case 'helpCatalog':      startHelpCatalogTour(history, setTourMenuOpen); break;
      case 'timezone':         startTimezoneTour(history); break;
      default: break;
    }
  }

  if (!release) return null;

  const canTour = user?.access_type_cd !== 'c';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography>
        <Typography variant="h6">{release.title}</Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
          {release.subtitle}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {release.features.map((feature, i) => (
          <div key={i}>
            {i > 0 && <Divider style={{ margin: '12px 0' }} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <Typography variant="subtitle2" gutterBottom style={{ flex: 1 }}>
                {feature.title}
              </Typography>
              {canTour && feature.tourKey && (
                <Button
                  size="small"
                  color="primary"
                  style={{ fontSize: 11, padding: '2px 8px', flexShrink: 0, marginTop: -2 }}
                  onClick={() => handleFeatureTour(feature.tourKey)}
                >
                  Tour →
                </Button>
              )}
            </div>
            <Typography variant="body2" color="textSecondary">
              {feature.description}
            </Typography>
          </div>
        ))}
      </DialogContent>

      <DialogActions style={{ justifyContent: 'space-between' }}>
        {canTour && (
          <Button color="primary" onClick={handleTour}>
            Full Tour
          </Button>
        )}
        <Button variant="contained" color="primary" onClick={handleClose}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
